// RevenueCatDebugScreen.tsx
// Debug panel for testing RevenueCat subscription integration

import React, { useState, useEffect } from 'react';
import { View, ScrollView, ActivityIndicator, Pressable, Platform, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import {
  PageContainer,
  ContentContainer,
  Text,
  Button,
  Card,
} from '../../components/ui';
import { Ionicons } from '@expo/vector-icons';
import { Theme } from '../../utils/theme';
import { useSubscription } from '../../hooks/useSubscription';
import { SubscriptionService, PurchasesOfferings, PurchasesPackage } from '../../services/subscriptionService';

export const RevenueCatDebugScreen: React.FC = () => {
  const navigation = useNavigation();
  const { 
    tier, 
    expiresAt, 
    tierDisplayName, 
    expiryText,
    refreshSubscription,
    restorePurchases,
  } = useSubscription();
  
  const [isLoading, setIsLoading] = useState(true);
  const [offerings, setOfferings] = useState<PurchasesOfferings | null>(null);
  const [customerInfo, setCustomerInfo] = useState<any>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);

  useEffect(() => {
    loadDebugInfo();
  }, []);

  const loadDebugInfo = async () => {
    setIsLoading(true);
    try {
      // Load offerings
      const offeringsResult = await SubscriptionService.getOfferings();
      if (offeringsResult.success && offeringsResult.data) {
        setOfferings(offeringsResult.data);
      }

      // Load customer info
      const customerResult = await SubscriptionService.getCustomerInfo(true);
      if (customerResult.success && customerResult.data) {
        setCustomerInfo(customerResult.data);
      }
    } catch (error) {
      console.error('[RevenueCatDebug] Load error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = async (pkg: PurchasesPackage) => {
    if (Platform.OS === 'web') {
      Toast.show({
        type: 'error',
        text1: 'Not Available on Web',
        text2: 'Use iOS or Android to test purchases',
      });
      return;
    }

    setIsPurchasing(true);
    try {
      const result = await SubscriptionService.purchasePackage(pkg);
      if (result.success) {
        Toast.show({
          type: 'success',
          text1: 'Purchase Successful',
          text2: `Purchased: ${pkg.identifier}`,
        });
        await loadDebugInfo();
      } else {
        Toast.show({
          type: 'error',
          text1: 'Purchase Failed',
          text2: result.error,
        });
      }
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Purchase Error',
        text2: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleRestore = async () => {
    setIsPurchasing(true);
    try {
      const success = await restorePurchases('manual');
      if (success) {
        Toast.show({
          type: 'success',
          text1: 'Purchases Restored',
        });
        await loadDebugInfo();
      } else {
        Toast.show({
          type: 'error',
          text1: 'No Purchases Found',
        });
      }
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Restore Failed',
      });
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleRefresh = async () => {
    await refreshSubscription();
    await loadDebugInfo();
  };

  const formatDate = (timestamp: string | null | undefined): string => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Pressable 
          style={styles.closeButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="close" size={24} color={Theme.colors.textPrimary} />
        </Pressable>
        
        <ContentContainer scrollable={false} style={{ justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <ActivityIndicator size="large" color={Theme.colors.primary} />
          <Text variant="body1" style={{ marginTop: Theme.spacing.md }}>
            Loading RevenueCat data...
          </Text>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Pressable 
        style={styles.closeButton}
        onPress={() => navigation.goBack()}
      >
        <Ionicons name="close" size={24} color={Theme.colors.text} />
      </Pressable>
      
      <ScrollView 
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: Theme.spacing.xl }}
        showsVerticalScrollIndicator={false}
      >
        <ContentContainer>
          {/* Header */}
          <View style={{ paddingTop: Theme.spacing.lg, marginBottom: Theme.spacing.md }}>
            <Pressable onPress={() => navigation.goBack()}>
              <Text variant="body1" style={{ color: Theme.colors.primary }}>
                ← Back
              </Text>
            </Pressable>
            <Text variant="h1" style={{ marginTop: Theme.spacing.sm }}>
              RevenueCat Debug
            </Text>
            <Text variant="body1" style={{ color: Theme.colors.textSecondary, marginTop: Theme.spacing.xs }}>
              Test subscription features
            </Text>
          </View>

          {/* Current Subscription Status */}
          <Card style={{ marginBottom: Theme.spacing.md }}>
            <View style={{ padding: Theme.spacing.md }}>
              <Text variant="h3" style={{ marginBottom: Theme.spacing.md }}>
                Current Subscription
              </Text>
              
              <InfoRow label="Tier" value={tierDisplayName} />
              <InfoRow label="Tier ID" value={tier} />
              <InfoRow 
                label="Expires At" 
                value={expiresAt ? formatDate(new Date(expiresAt * 1000).toISOString()) : 'Never'} 
              />
              <InfoRow label="Status" value={expiryText || 'Active'} />
              
              <Button
                title="Refresh Subscription"
                onPress={() => handleRefresh()}
                variant="secondary"
                style={{ marginTop: Theme.spacing.md }}
              />
            </View>
          </Card>

          {/* Customer Info */}
          {customerInfo && (
            <Card style={{ marginBottom: Theme.spacing.md }}>
              <View style={{ padding: Theme.spacing.md }}>
                <Text variant="h3" style={{ marginBottom: Theme.spacing.md }}>
                  Customer Info (Raw)
                </Text>
                
                <InfoRow label="Customer ID" value={customerInfo.originalAppUserId || 'N/A'} />
                <InfoRow 
                  label="First Seen" 
                  value={formatDate(customerInfo.firstSeen)} 
                />
                <InfoRow 
                  label="Request Date" 
                  value={formatDate(customerInfo.requestDate)} 
                />
                
                <Text variant="body1" style={{ marginTop: Theme.spacing.md, marginBottom: Theme.spacing.sm, fontWeight: 'bold' }}>
                  Active Entitlements:
                </Text>
                {Object.keys(customerInfo.entitlements.active).length > 0 ? (
                  Object.entries(customerInfo.entitlements.active).map(([key, value]: [string, any]) => (
                    <View 
                      key={key}
                      style={{ 
                        backgroundColor: Theme.colors.background,
                        padding: Theme.spacing.sm,
                        borderRadius: Theme.borderRadius.sm,
                        marginBottom: Theme.spacing.xs,
                      }}
                    >
                      <Text variant="body1" style={{ color: Theme.colors.primary, fontWeight: 'bold' }}>
                        {key}
                      </Text>
                      <Text variant="caption" style={{ color: Theme.colors.textSecondary }}>
                        Product: {value.productIdentifier}
                      </Text>
                      <Text variant="caption" style={{ color: Theme.colors.textSecondary }}>
                        Expires: {formatDate(value.expirationDate)}
                      </Text>
                      <Text variant="caption" style={{ color: Theme.colors.textSecondary }}>
                        Will Renew: {value.willRenew ? 'Yes' : 'No'}
                      </Text>
                    </View>
                  ))
                ) : (
                  <Text variant="body1" style={{ color: Theme.colors.textSecondary }}>
                    No active entitlements
                  </Text>
                )}
              </View>
            </Card>
          )}

          {/* Available Offerings */}
          {offerings && offerings.current && (
            <Card style={{ marginBottom: Theme.spacing.md }}>
              <View style={{ padding: Theme.spacing.md }}>
                <Text variant="h3" style={{ marginBottom: Theme.spacing.md }}>
                  Available Packages
                </Text>
                
                {offerings.current.availablePackages.map((pkg: any) => (
                  <View
                    key={pkg.identifier}
                    style={{
                      backgroundColor: Theme.colors.background,
                      padding: Theme.spacing.md,
                      borderRadius: Theme.borderRadius.md,
                      marginBottom: Theme.spacing.sm,
                    }}
                  >
                    <Text variant="body1" style={{ marginBottom: Theme.spacing.xs, fontWeight: 'bold' }}>
                      {pkg.identifier}
                    </Text>
                    <InfoRow label="Type" value={pkg.packageType} />
                    <InfoRow label="Product ID" value={pkg.product.identifier} />
                    <InfoRow label="Title" value={pkg.product.title} />
                    <InfoRow label="Description" value={pkg.product.description} />
                    <InfoRow label="Price" value={pkg.product.priceString} />
                    <InfoRow label="Currency" value={pkg.product.currencyCode} />
                    
                    <Button
                      title={isPurchasing ? 'Processing...' : `Purchase ${pkg.product.priceString}`}
                      onPress={() => handlePurchase(pkg)}
                      disabled={isPurchasing || Platform.OS === 'web'}
                      style={{ marginTop: Theme.spacing.sm }}
                      variant="secondary"
                    />
                  </View>
                ))}
              </View>
            </Card>
          )}

          {/* All Offerings */}
          {offerings && (
            <Card style={{ marginBottom: Theme.spacing.md }}>
              <View style={{ padding: Theme.spacing.md }}>
                <Text variant="h3" style={{ marginBottom: Theme.spacing.md }}>
                  All Offerings
                </Text>
                
                <InfoRow 
                  label="Current Offering ID" 
                  value={offerings.current?.identifier || 'None'} 
                />
                
                {Object.entries(offerings.all).length > 0 ? (
                  Object.entries(offerings.all).map(([key, offering]: [string, any]) => (
                    <View 
                      key={key}
                      style={{ 
                        backgroundColor: Theme.colors.background,
                        padding: Theme.spacing.sm,
                        borderRadius: Theme.borderRadius.sm,
                        marginTop: Theme.spacing.sm,
                      }}
                    >
                      <Text variant="body1" style={{ fontWeight: 'bold' }}>{key}</Text>
                      <Text variant="caption" style={{ color: Theme.colors.textSecondary }}>
                        {offering.availablePackages.length} packages available
                      </Text>
                    </View>
                  ))
                ) : (
                  <Text variant="body1" style={{ color: Theme.colors.textSecondary }}>
                    No offerings configured
                  </Text>
                )}
              </View>
            </Card>
          )}

          {/* Actions */}
          <Card style={{ marginBottom: Theme.spacing.md }}>
            <View style={{ padding: Theme.spacing.md }}>
              <Text variant="h3" style={{ marginBottom: Theme.spacing.md }}>
                Actions
              </Text>
              
              <Button
                title="Restore Purchases"
                onPress={() => handleRestore()}
                disabled={isPurchasing || Platform.OS === 'web'}
                variant="secondary"
                style={{ marginBottom: Theme.spacing.sm }}
              />
              
              <Button
                title="Open Paywall Screen"
                onPress={() => navigation.navigate('Paywall' as never)}
                variant="secondary"
              />
            </View>
          </Card>

          {/* Platform Warning */}
          {Platform.OS === 'web' && (
            <Card style={{ backgroundColor: Theme.colors.error + '20', borderColor: Theme.colors.error }}>
              <View style={{ padding: Theme.spacing.md }}>
                <Text variant="body1" style={{ color: Theme.colors.error, marginBottom: Theme.spacing.xs, fontWeight: 'bold' }}>
                  ⚠️ Web Platform Limitation
                </Text>
                <Text variant="body1" style={{ color: Theme.colors.textSecondary }}>
                  RevenueCat purchases are not available on web. Please test on iOS or Android device/simulator.
                </Text>
              </View>
            </Card>
          )}
        </ContentContainer>
      </ScrollView>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  closeButton: {
    position: 'absolute',
    top: 50,
    right: 16,
    zIndex: 1000,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Theme.colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
});

// Helper component for displaying key-value pairs
const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <View 
    style={{ 
      flexDirection: 'row', 
      justifyContent: 'space-between',
      marginBottom: Theme.spacing.xs,
      paddingVertical: Theme.spacing.xs,
    }}
  >
    <Text variant="body1" style={{ color: Theme.colors.textSecondary, flex: 1 }}>
      {label}:
    </Text>
    <Text 
      variant="body1" 
      style={{ 
        flex: 2, 
        textAlign: 'right',
        fontFamily: 'Courier',
      }}
    >
      {value}
    </Text>
  </View>
);

